import React, { useEffect, useMemo, useState } from 'react';
import MetaTags from '@/components/meta/MetaTags';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface ClaimRow {
  id: string;
  user_id: string;
  rarity: string;
  claimed_at: string;
}

interface ProfileLite {
  id: string;
  username: string | null;
  display_name: string | null;
}

const rarityVariant = (rarity: string): 'secondary' | 'default' | 'outline' | 'destructive' => {
  switch (rarity) {
    case 'common':
      return 'secondary';
    case 'uncommon':
      return 'outline';
    case 'rare':
      return 'default';
    case 'legendary':
      return 'destructive';
    default:
      return 'secondary';
  }
};

const DropsClaims: React.FC = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [collectionName, setCollectionName] = useState<string>('Collection');

  useEffect(() => {
    const load = async () => {
      if (!collectionId) return;
      setLoading(true);
      try {
        // Load collection info (name)
        const { data: coll } = await supabase
          .from('drop_collections')
          .select('id, name')
          .eq('id', collectionId)
          .maybeSingle();
        if (coll?.name) setCollectionName(coll.name);

        // Load claims
        const { data: claimRows } = await supabase
          .from('drop_claims')
          .select('id, user_id, rarity, claimed_at')
          .eq('collection_id', collectionId)
          .order('claimed_at', { ascending: false });

        const list = (claimRows || []) as ClaimRow[];
        setClaims(list);

        const userIds = Array.from(new Set(list.map((c) => c.user_id)));
        if (userIds.length) {
          const { data: profs } = await supabase
            .from('public_profiles')
            .select('id, username, display_name')
            .in('id', userIds);
          const map: Record<string, ProfileLite> = {};
          (profs || []).forEach((p) => (map[p.id] = p as ProfileLite));
          setProfiles(map);
        } else {
          setProfiles({});
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [collectionId]);

  const total = claims.length;
  const byRarity = useMemo(() => {
    const agg: Record<string, number> = {};
    claims.forEach((c) => (agg[c.rarity] = (agg[c.rarity] || 0) + 1));
    return agg;
  }, [claims]);

  return (
    <main className="container mx-auto max-w-5xl px-4 py-8">
      <MetaTags title={`Claims – ${collectionName} – FOMO`} description={`View who claimed items from ${collectionName}.`} />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">{collectionName} – Claims</h1>
        <Button variant="secondary" onClick={() => navigate('/drops')}>Back to Drops</Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">Total claims: {total}</Badge>
            {Object.entries(byRarity).map(([rarity, count]) => (
              <Badge key={rarity} variant={rarityVariant(rarity)} className="capitalize">
                {rarity}: {count}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Claimants</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading claims…</div>
          ) : claims.length === 0 ? (
            <div className="text-sm text-muted-foreground">No claims yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Rarity</TableHead>
                    <TableHead>Claimed At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {claims.map((c) => {
                    const p = profiles[c.user_id];
                    const name = p?.display_name || p?.username || c.user_id.slice(0, 8);
                    return (
                      <TableRow key={c.id}>
                        <TableCell>{name}</TableCell>
                        <TableCell>
                          <Badge variant={rarityVariant(c.rarity)} className="capitalize">{c.rarity}</Badge>
                        </TableCell>
                        <TableCell>{new Date(c.claimed_at).toLocaleString()}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
};

export default DropsClaims;
